import { isArray, isEmpty, isNil, isObject, isString } from 'lodash';
import { ErrorMessage, Rule } from '~domain/model/core/validation/rule';
import { ValidationContext } from '~domain/model/core/validation/validation-context';
import { Validator } from '~domain/model/core/validation/validator';
import { hasValidationErrors } from '~domain/validators/core/detect-validation-errors';

export abstract class BaseValidator<T> implements Validator<T> {
  protected abstract rules: Rule<T>[];

  // Loop through all rules to validate data
  public validate(value: T, context?: ValidationContext): ErrorMessage {
    for (const rule of this.rules) {
      const errorMessage: ErrorMessage = rule.call(this, value, context);
      if (hasValidationErrors(errorMessage)) return errorMessage;
    }
  }

  // Create a rule that validates the value is defined, is an array and has the expected length
  protected createArrayRule(minLength: number, maxLength: number, message?: ErrorMessage): Rule<T> {
    return (value: T) => {
      if (!isArray(value)) return message || 'validation.error.required';
      if (value.length < minLength || value.length > maxLength) return message || 'validation.error.required';
    };
  }

  // Create a rule that validates the value is defined
  protected createNullRule(message?: ErrorMessage): Rule<T> {
    const defaultMessage: ErrorMessage = message || 'validation.error.required';
    return (value: T) => {
      // Invalidates null or undefined values
      if (isNil(value)) return defaultMessage;
      // Invalidates '' string values
      if (isString(value) && isEmpty(value)) return defaultMessage;
    };
  }

  // Create a rule that validates the value is an object
  protected createObjectRule(message?: ErrorMessage): Rule<T> {
    return (value: T) => {
      if (!isObject(value)) return message || 'validation.error.required';
    };
  }

  // Create a rule that validates the value is an array and has the expected length
  protected createOptionalArrayRule(minLength: number, maxLength: number, message?: ErrorMessage): Rule<T> {
    return (value: T) => {
      if (isNil(value)) return;

      const arrayRule = this.createArrayRule(minLength, maxLength, message);
      return arrayRule(value);
    };
  }
}
